import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "space-y-4 sm:space-y-6" }
const _hoisted_2 = { class: "card mb-6" }
const _hoisted_3 = { class: "card-body" }
const _hoisted_4 = { class: "flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6" }
const _hoisted_5 = { class: "h-16 w-16 sm:h-20 sm:w-20 rounded-full overflow-hidden bg-secondary flex items-center justify-center text-white text-2xl sm:text-3xl font-semibold shadow-lg transform transition-transform duration-300 hover:scale-105" }
const _hoisted_6 = ["src", "alt"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { class: "text-center sm:text-left" }
const _hoisted_9 = { class: "text-xl sm:text-2xl font-bold text-gray-900 dark:text-white" }
const _hoisted_10 = { class: "text-sm sm:text-base text-secondary-text dark:text-secondary-dark-text mt-1" }
const _hoisted_11 = {
  key: 0,
  class: "mt-6 flex flex-col justify-center items-center py-12"
}
const _hoisted_12 = {
  key: 1,
  class: "mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
}
const _hoisted_13 = { class: "flex items-center" }
const _hoisted_14 = { class: "text-red-700 dark:text-red-300 font-medium" }
const _hoisted_15 = {
  key: 2,
  class: "space-y-4 sm:space-y-6"
}
const _hoisted_16 = ["onChange"]
const _hoisted_17 = {
  key: 0,
  class: "flex items-center gap-4 text-sm"
}
const _hoisted_18 = { class: "flex items-center gap-2" }
const _hoisted_19 = { class: "text-gray-600 dark:text-gray-400" }
const _hoisted_20 = { class: "font-medium text-green-600 dark:text-green-400" }
const _hoisted_21 = { class: "flex items-center gap-2" }
const _hoisted_22 = { class: "text-gray-600 dark:text-gray-400" }
const _hoisted_23 = { class: "font-medium text-red-600 dark:text-red-400" }
const _hoisted_24 = { class: "break-all" }
const _hoisted_25 = { class: "break-all" }
const _hoisted_26 = { class: "flex items-center" }
const _hoisted_27 = { class: "h-10 w-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center" }
const _hoisted_28 = ["src", "alt"]
const _hoisted_29 = {
  key: 1,
  class: "text-gray-500 text-sm"
}
const _hoisted_30 = { class: "flex justify-center" }
const _hoisted_31 = { class: "flex justify-center space-x-3" }
const _hoisted_32 = ["onClick", "title"]
const _hoisted_33 = ["onClick"]
const _hoisted_34 = ["onClick"]
const _hoisted_35 = ["onClick"]
const _hoisted_36 = { class: "flex justify-end space-x-3" }
const _hoisted_37 = ["onClick", "title"]
const _hoisted_38 = { class: "hidden sm:inline" }
const _hoisted_39 = ["onClick"]
const _hoisted_40 = ["onClick"]
const _hoisted_41 = ["onClick"]

import { computed, onMounted, ref } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useEmployees } from '@/composables/employee/useEmployees'
import { useEmployeeStore } from '@/stores/employee'
import { useThemeStore } from '@/stores/theme'
import { employeeService } from '@/services/user'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import RegisterEmployeeModal from '@/components/modals/RegisterEmployee.vue'
import EditEmployeeModal from '@/components/modals/EditEmployee.vue'
import DeleteEmployeeModal from '@/components/modals/DeleteEmployee.vue'
import ViewEmployeeDetails from '@/components/modals/ViewEmployeeDetails.vue'
import DataTable from '@/components/common/DataTable.vue'
import { 
  AlertCircle,
  Plus,
  Lock,
  LockOpen,
  Eye,
  Edit3,
  Trash2,
  Users
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeView',
  setup(__props) {

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
useTitle(`Employees - ${appTitle.value}`)

const { userInitials, userName, userEmail, userImage } = useUser()

// Toast service for notifications
const toast = useToastService()

// Theme store for dark mode detection
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// employee management
const {
  employees,
  isLoading,
  error,
  fetchEmployees,
  setSearch,
  setStatusFilter,
  setPagination,
  activeEmployees,
  inactiveEmployees,
  statusFilter: storedStatusFilter
} = useEmployees()

// Modal state
const isRegisterModalVisible = ref(false)
const isEditModalVisible = ref(false)
const isDeleteModalVisible = ref(false)
const isViewDetailsModalVisible = ref(false)
const selectedEmployee = ref<employee | null>(null)
const selectedEmployeeForDelete = ref<employee | null>(null)
const selectedEmployeeForView = ref<employee | null>(null)

// Local filter state - initialize from stored value
const statusFilter = ref(storedStatusFilter.value)

// Table configuration
const tableColumns = [
  { key: 'profile', label: 'Profile', sortable: false, width: '90px' },
  { key: 'first_name', label: 'First Name', sortable: true },
  { key: 'last_name', label: 'Last Name', sortable: true },
  { key: 'middle_name', label: 'Middle Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'status', label: 'Status', sortable: true, align: 'center' as const, width: '90px' }
]

const sortableColumns = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' }
]

const mobileColumns = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'middle_name', label: 'Middle Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' }
]

// DataTable event handlers
const handleSearch = (query: string) => {
  setSearch(query)
}

const employeeStore = useEmployeeStore()

const handleSort = (sort: { field: string; direction: 'asc' | 'desc' }) => {
  employeeStore.setFilters({ sortBy: sort.field, sortDirection: sort.direction })
}

const handleFilter = (filters: Record<string, any>) => {
  if (filters.status !== undefined) {
    statusFilter.value = filters.status
    setStatusFilter(filters.status as 'Active' | 'Inactive' | '')
  }
}

const handlePageChange = (page: number) => {
  // The DataTable component handles pagination internally
  console.log('Page change:', page)
}

const handleItemsPerPageChange = (itemsPerPage: number) => {
  setPagination({ itemsPerPage })
}

// Helper functions
const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  if (target) {
    target.style.display = 'none'
  }
}

// Modal handlers
const handleRegistrationSuccess = () => {
  fetchEmployees()
}

const handleEditSuccess = () => {
  fetchEmployees()
}

const handleDeleteSuccess = () => {
  // Clear search filter to show all employees after deletion
  setSearch('')
  fetchEmployees()
}

// employee action functions
const registerNewUser = () => {
  isRegisterModalVisible.value = true
}

const toggleUserStatus = async (user: any) => {
  try {
    const newStatus = user.status === 'Active' ? 0 : 1
    const action = newStatus === 1 ? 'activated' : 'deactivated'

    await employeeService.updateUserStatus(user.user_details_id, newStatus)

    // Update the user status locally without refetching all data
    user.status = newStatus === 1 ? 'Active' : 'Inactive'

    toast.success(`${user.first_name} ${user.last_name} has been ${action}.`)
  } catch (error: any) {
    console.error('Error toggling user status:', error)
    toast.error('Failed to update user status. Please try again.')
  }
}

const viewEmployee = (user: any) => {
  selectedEmployeeForView.value = user
  isViewDetailsModalVisible.value = true
}

const handleViewDetailsClose = () => {
  isViewDetailsModalVisible.value = false
  selectedEmployeeForView.value = null
}

const editEmployee = (user: any) => {
  selectedEmployee.value = user
  isEditModalVisible.value = true
}

const deleteUser = (user: employee) => {
  selectedEmployeeForDelete.value = user
  isDeleteModalVisible.value = true
}

// Load employees on component mount
onMounted(async () => {
  await fetchEmployees()
})

return (_ctx: any,_cache: any) => {
  const _component_vue_loaders = _resolveComponent("vue-loaders")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              (_unref(userImage))
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    src: _unref(userImage),
                    alt: _unref(userName),
                    class: "w-full h-full object-cover"
                  }, null, 8, _hoisted_6))
                : (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_unref(userInitials)), 1))
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("h1", _hoisted_9, " Welcome, " + _toDisplayString(_unref(userName)) + "! ", 1),
              _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(userEmail)), 1)
            ])
          ])
        ])
      ])
    ]),
    _createVNode(RegisterEmployeeModal, {
      "is-visible": isRegisterModalVisible.value,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (isRegisterModalVisible.value = false)),
      onSuccess: handleRegistrationSuccess
    }, null, 8, ["is-visible"]),
    _createVNode(EditEmployeeModal, {
      "is-visible": isEditModalVisible.value,
      employee: selectedEmployee.value,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (isEditModalVisible.value = false)),
      onSuccess: handleEditSuccess
    }, null, 8, ["is-visible", "employee"]),
    _createVNode(DeleteEmployeeModal, {
      "is-visible": isDeleteModalVisible.value,
      employee: selectedEmployeeForDelete.value,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (isDeleteModalVisible.value = false)),
      onSuccess: handleDeleteSuccess
    }, null, 8, ["is-visible", "employee"]),
    _createVNode(ViewEmployeeDetails, {
      "is-visible": isViewDetailsModalVisible.value,
      employee: selectedEmployeeForView.value || undefined,
      onClose: handleViewDetailsClose
    }, null, 8, ["is-visible", "employee"]),
    (_unref(isLoading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _createVNode(_component_vue_loaders, {
            name: "line-scale",
            color: isDarkMode.value ? '#818cf8' : '#4f46e5',
            scale: "1.2",
            class: "mb-4"
          }, null, 8, ["color"]),
          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-gray-900 dark:text-white" }, "Loading employees...", -1))
        ]))
      : (_unref(error))
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              _createVNode(_unref(AlertCircle), { class: "h-5 w-5 text-red-500 mr-2" }),
              _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(error)), 1)
            ]),
            _createElementVNode("button", {
              onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(fetchEmployees) && _unref(fetchEmployees)(...args))),
              class: "mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
            }, " Try again ")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_15, [
            _createVNode(DataTable, {
              data: _unref(employees),
              columns: tableColumns,
              loading: _unref(isLoading),
              "sortable-columns": sortableColumns,
              "mobile-columns": mobileColumns,
              "item-key": "user_details_id",
              "item-label": "employees",
              "search-placeholder": "Search by name, email...",
              "summary-icon": _unref(Users),
              "show-search": true,
              "show-filters": true,
              "show-pagination": true,
              "show-summary": true,
              "items-per-page": 10,
              "items-per-page-options": [5, 10, 25, 50],
              "table-id": "employee-table",
              onSearch: handleSearch,
              onSort: handleSort,
              onFilter: handleFilter,
              onPageChange: handlePageChange,
              onItemsPerPageChange: handleItemsPerPageChange
            }, {
              actions: _withCtx(() => [
                _createElementVNode("button", {
                  onClick: registerNewUser,
                  class: "btn btn-primary btn-sm flex items-center gap-2"
                }, [
                  _createVNode(_unref(Plus), { class: "h-4 w-4" }),
                  _cache[6] || (_cache[6] = _createElementVNode("span", { class: "hidden sm:inline" }, "Register New", -1))
                ])
              ]),
              filters: _withCtx(({ updateFilter }) => [
                _createElementVNode("div", null, [
                  _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, " Status ", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((statusFilter).value = $event)),
                    onChange: ($event: any) => (updateFilter('status', statusFilter.value)),
                    class: "block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
                  }, [...(_cache[7] || (_cache[7] = [
                    _createElementVNode("option", { value: "" }, "All Status", -1),
                    _createElementVNode("option", { value: "Active" }, "Active", -1),
                    _createElementVNode("option", { value: "Inactive" }, "Inactive", -1)
                  ]))], 40, _hoisted_16), [
                    [_vModelSelect, statusFilter.value]
                  ])
                ])
              ]),
              summary: _withCtx(({ totalItems }) => [
                (totalItems > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                      _createElementVNode("div", _hoisted_18, [
                        _cache[10] || (_cache[10] = _createElementVNode("div", { class: "w-2 h-2 bg-green-500 rounded-full" }, null, -1)),
                        _createElementVNode("span", _hoisted_19, [
                          _createElementVNode("span", _hoisted_20, _toDisplayString(_unref(activeEmployees)), 1),
                          _cache[9] || (_cache[9] = _createTextVNode(" active ", -1))
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_21, [
                        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "w-2 h-2 bg-red-500 rounded-full" }, null, -1)),
                        _createElementVNode("span", _hoisted_22, [
                          _createElementVNode("span", _hoisted_23, _toDisplayString(_unref(inactiveEmployees)), 1),
                          _cache[11] || (_cache[11] = _createTextVNode(" inactive ", -1))
                        ])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              "cell-email": _withCtx(({ value }) => [
                _createElementVNode("span", _hoisted_24, _toDisplayString(value), 1)
              ]),
              "mobile-email": _withCtx(({ value }) => [
                _createElementVNode("span", _hoisted_25, _toDisplayString(value), 1)
              ]),
              "cell-middle_name": _withCtx(({ value }) => [
                _createElementVNode("span", {
                  class: _normalizeClass(value ? '' : 'text-gray-400 dark:text-gray-500 italic')
                }, _toDisplayString(value || 'Not stated'), 3)
              ]),
              "mobile-middle_name": _withCtx(({ value }) => [
                _createElementVNode("span", {
                  class: _normalizeClass(value ? '' : 'text-gray-400 dark:text-gray-500 italic')
                }, _toDisplayString(value || 'Not stated'), 3)
              ]),
              "mobile-status": _withCtx(({ item }) => [
                _createElementVNode("span", {
                  class: _normalizeClass([
            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
            item.status === 'Active'
              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
              : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
          ])
                }, _toDisplayString(item.status), 3)
              ]),
              "cell-profile": _withCtx(({ item }) => [
                _createElementVNode("div", _hoisted_26, [
                  _createElementVNode("div", _hoisted_27, [
                    (item.user_image)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: item.user_image,
                          alt: `${item.first_name} ${item.last_name}`,
                          class: "h-full w-full object-cover",
                          onError: handleImageError
                        }, null, 40, _hoisted_28))
                      : (_openBlock(), _createElementBlock("span", _hoisted_29, _toDisplayString(item.first_name?.charAt(0) || '') + _toDisplayString(item.last_name?.charAt(0) || ''), 1))
                  ])
                ])
              ]),
              "cell-status": _withCtx(({ item }) => [
                _createElementVNode("div", _hoisted_30, [
                  _createElementVNode("span", {
                    class: _normalizeClass([
              'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
              item.status === 'Active'
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            ])
                  }, _toDisplayString(item.status), 3)
                ])
              ]),
              "row-actions": _withCtx(({ item }) => [
                _createElementVNode("div", _hoisted_31, [
                  _createElementVNode("button", {
                    class: _normalizeClass([
              'transition duration-200 transform',
              item.status === 'Active'
                ? 'text-secondary dark:text-secondary-dark dark:hover:text-gray-500 hover:text-gray-900'
                : 'text-primary dark:text-primary-dark-text hover:text-gray-900 dark:hover:text-gray-500'
            ]),
                    onClick: ($event: any) => (toggleUserStatus(item)),
                    title: item.status === 'Active' ? 'Deactivate User' : 'Activate User'
                  }, [
                    (item.status === 'Active')
                      ? (_openBlock(), _createBlock(_unref(LockOpen), {
                          key: 0,
                          class: "h-5 w-5"
                        }))
                      : (_openBlock(), _createBlock(_unref(Lock), {
                          key: 1,
                          class: "h-5 w-5"
                        }))
                  ], 10, _hoisted_32),
                  _createElementVNode("button", {
                    class: "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition",
                    onClick: ($event: any) => (viewEmployee(item)),
                    title: "View User"
                  }, [
                    _createVNode(_unref(Eye), { class: "h-5 w-5" })
                  ], 8, _hoisted_33),
                  _createElementVNode("button", {
                    class: "text-blue-500 hover:text-blue-700 transition",
                    onClick: ($event: any) => (editEmployee(item)),
                    title: "Edit User"
                  }, [
                    _createVNode(_unref(Edit3), { class: "h-5 w-5" })
                  ], 8, _hoisted_34),
                  _createElementVNode("button", {
                    class: "text-red-500 hover:text-red-700 transition",
                    onClick: ($event: any) => (deleteUser(item)),
                    title: "Delete User"
                  }, [
                    _createVNode(_unref(Trash2), { class: "h-5 w-5" })
                  ], 8, _hoisted_35)
                ])
              ]),
              "mobile-actions": _withCtx(({ item }) => [
                _createElementVNode("div", _hoisted_36, [
                  _createElementVNode("button", {
                    class: _normalizeClass([
              'transition duration-200 transform flex items-center gap-1 px-2 py-1 rounded text-sm',
              item.status === 'Active'
                ? 'text-secondary dark:text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
            ]),
                    onClick: ($event: any) => (toggleUserStatus(item)),
                    title: item.status === 'Active' ? 'Deactivate User' : 'Activate User'
                  }, [
                    (item.status === 'Active')
                      ? (_openBlock(), _createBlock(_unref(LockOpen), {
                          key: 0,
                          class: "h-4 w-4"
                        }))
                      : (_openBlock(), _createBlock(_unref(Lock), {
                          key: 1,
                          class: "h-4 w-4"
                        })),
                    _createElementVNode("span", _hoisted_38, _toDisplayString(item.status === 'Active' ? 'Deactivate' : 'Activate'), 1)
                  ], 10, _hoisted_37),
                  _createElementVNode("button", {
                    class: "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700",
                    onClick: ($event: any) => (viewEmployee(item)),
                    title: "View employee"
                  }, [
                    _createVNode(_unref(Eye), { class: "h-4 w-4" }),
                    _cache[13] || (_cache[13] = _createElementVNode("span", { class: "hidden sm:inline" }, "View", -1))
                  ], 8, _hoisted_39),
                  _createElementVNode("button", {
                    class: "text-blue-500 hover:text-blue-700 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20",
                    onClick: ($event: any) => (editEmployee(item)),
                    title: "Edit employee"
                  }, [
                    _createVNode(_unref(Edit3), { class: "h-4 w-4" }),
                    _cache[14] || (_cache[14] = _createElementVNode("span", { class: "hidden sm:inline" }, "Edit", -1))
                  ], 8, _hoisted_40),
                  _createElementVNode("button", {
                    class: "text-red-500 hover:text-red-700 transition flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20",
                    onClick: ($event: any) => (deleteUser(item)),
                    title: "Delete employee"
                  }, [
                    _createVNode(_unref(Trash2), { class: "h-4 w-4" }),
                    _cache[15] || (_cache[15] = _createElementVNode("span", { class: "hidden sm:inline" }, "Delete", -1))
                  ], 8, _hoisted_41)
                ])
              ]),
              "empty-actions": _withCtx(() => [
                _createElementVNode("button", {
                  onClick: registerNewUser,
                  class: "btn btn-primary btn-sm"
                }, "Register")
              ]),
              _: 1
            }, 8, ["data", "loading", "summary-icon"])
          ]))
  ], 64))
}
}

})